import { useMemo } from 'react';
import * as THREE from 'three';
import { ThreeElements } from '@react-three/fiber';
import { getObjects, isMesh, filterGeometryAttributes } from '@slope/three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type SurfaceMeshProps = Omit<ThreeElements['mesh'], 'layers'> & {
  object: THREE.Object3D;
  layers?: ('semi' | 'fairway' | 'green' | 'bunker' | 'tee')[];
};

export const SurfaceMesh: React.FC<SurfaceMeshProps> = ({
  object,
  children,
  layers = ['semi', 'fairway', 'green', 'bunker', 'tee'],
  ...restProps
}) => {
  const geometry = useMemo(() => {
    const meshes = getObjects(
      object,
      (node) => isMesh(node) && new RegExp(layers.join('|'), 'i').test(node.name),
    ) as THREE.Mesh[];

    if (meshes.length === 0) {
      console.warn('SurfaceMesh: No matching meshes found');
      return null;
    }

    const geometries: THREE.BufferGeometry[] = meshes.map((mesh) => {
      const geom = mesh.geometry.clone();
      geom.applyMatrix4(mesh.matrixWorld);
      // Filter to only keep position and normal attributes
      return filterGeometryAttributes(geom);
    });
    return mergeGeometries(geometries, false);
  }, [object, layers]);

  if (geometry === null) {
    return null;
  }

  return (
    <mesh name="surface-mesh" geometry={geometry} {...restProps}>
      {children}
    </mesh>
  );
};
